/**
 * Re-serialize HTML into the browser's one canonical spelling: parse into a DOM
 * (which discards source formatting — inter-attribute whitespace and newlines
 * aren't part of the tree) then serialize via the HTML serialization algorithm
 * (one space between attributes, void elements with no trailing slash, an
 * explicit close tag on every non-void element — so `<polygon/>` becomes
 * `<polygon></polygon>` and `<meta />` becomes `<meta>`).
 *
 * Idempotent: canonicalizeHtml(canonicalizeHtml(x)) === canonicalizeHtml(x).
 *
 * Why this exists: get_video reads HTML back via outerHTML (the canonical
 * form), but edit_composition find-and-replaces against the stored string. If
 * the store kept the agent's raw formatting, an `old` string copied out of a
 * get_video result wouldn't be a substring of the edit target and the
 * edit would fail with "old not found". Keeping the store canonical on every
 * mutation makes the read and edit representations identical.
 *
 * The DOCTYPE is preserved — documentElement.outerHTML drops it, and a
 * doctype-less document renders in quirks mode, which would skew the preview and
 * the headless layout measurement.
 */
export function canonicalizeHtml(html: string): string {
    if (typeof DOMParser === 'undefined') return html;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return serializeDocument(doc);
}

/** Serialize a parsed document back to the canonical string form, DOCTYPE included. */
export function serializeDocument(doc: Document): string {
    const doctype = doc.doctype ? serializeDoctype(doc.doctype) : '';
    return doctype + doc.documentElement.outerHTML;
}

function serializeDoctype(dt: DocumentType): string {
    let out = `<!DOCTYPE ${dt.name}`;
    if (dt.publicId) out += ` PUBLIC "${dt.publicId}"`;
    if (dt.systemId) out += dt.publicId ? ` "${dt.systemId}"` : ` SYSTEM "${dt.systemId}"`;
    return `${out}>`;
}
